import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faCreditCard, faLock } from '@fortawesome/free-solid-svg-icons';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './StripePaymentModal.css';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#424770',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
      iconColor: '#9e2146',
    },
  },
  hidePostalCode: false,
};

const StripePaymentModal = ({ 
  isOpen, 
  onClose, 
  groupId, 
  eventId, 
  classId,
  onPaymentSuccess,
  isGroupDonation = false, 
  isClass = false,
  eventTitle = '',
  suggestedAmount = null
}) => {
  const [amount, setAmount] = useState('');
  const [dedication, setDedication] = useState('');
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [cardError, setCardError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [groupInfo, setGroupInfo] = useState(null);
  
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const { currentUser } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  // Fetch group information when modal opens
  useEffect(() => {
    if (isOpen && groupId) {
      fetchGroupInfo();
    }
  }, [isOpen, groupId]);

  // Set suggested amount when provided
  useEffect(() => {
    if (suggestedAmount && suggestedAmount > 0) {
      setAmount(suggestedAmount.toString());
    }
  }, [suggestedAmount]);

  const fetchGroupInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/groups/${groupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGroupInfo(data);
      }
    } catch (error) {
      console.error('Error fetching group info:', error);
    }
  };

  // Handle modal animations
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setPaymentError('');
      setPaymentSuccess(false);
      setCardError('');
      document.body.style.overflow = 'hidden';
      
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.classList.add('active');
        }
        if (overlayRef.current) {
          overlayRef.current.classList.add('active');
        }
      }, 10);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : '');
    setCardComplete(event.complete);
  };

  const handlePredefinedAmount = (predefinedAmount) => {
    setAmount(predefinedAmount.toString());
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || loading) {
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setPaymentError('Please enter a valid amount');
      return;
    }

    if (!cardComplete) {
      setPaymentError('Please complete your card information');
      return;
    }

    setLoading(true);
    setPaymentError('');

    try {
      // Create PaymentIntent on our server
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(amount),
          groupId,
          eventId: eventId || null,
          classId: classId || null,
          dedication: dedication || null,
          userId: currentUser?.id || null
        })
      });

      const paymentData = await response.json();

      if (!response.ok) {
        throw new Error(paymentData.error || 'Failed to create payment');
      }

      // Confirm payment with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        paymentData.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: currentUser?.name || 'Anonymous',
              email: currentUser?.email || null,
            },
          },
        }
      );

      if (error) {
        setPaymentError(error.message);
        setLoading(false);
      } else if (paymentIntent.status === 'succeeded') {
        setPaymentSuccess(true);
        setLoading(false);
        
        // Call success callback after a brief delay
        setTimeout(() => {
          if (onPaymentSuccess) {
            onPaymentSuccess({
              paymentIntentId: paymentIntent.id,
              amount: parseFloat(amount),
              dedication,
              groupId,
              eventId,
              classId
            });
          }
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.message || 'An error occurred while processing your payment');
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setIsClosing(true);
    
    if (modalRef.current) {
      modalRef.current.classList.remove('active');
    }
    
    if (overlayRef.current) {
      overlayRef.current.classList.remove('active');
    }
    
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      // Reset form state
      setAmount('');
      setDedication('');
      setPaymentError('');
      setPaymentSuccess(false);
      setCardError('');
    }, 300);
  };

  const handleClickOutside = (e) => {
    e.stopPropagation();
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      handleClose();
    }
  };

  const handleModalContentClick = (e) => {
    e.stopPropagation();
  };

  if (!isOpen && !isClosing) return null;

  const getModalTitle = () => {
    if (isGroupDonation) return 'Support your Community';
    if (isClass) return `Dedicate ${eventTitle || 'this class'}`;
    return `Sponsor ${eventTitle || 'this event'}`;
  };

  return ReactDOM.createPortal(
    <div className="sponsor-modal-overlay" ref={overlayRef} onClick={handleClickOutside}>
      <div className="sponsor-modal-container" ref={modalRef} onClick={handleModalContentClick}>
        <div className="sponsor-modal-content">
          <div className="sponsor-modal-header">
            <h2 className="sponsor-modal-title">
              {getModalTitle()}
            </h2>
            <button className="sponsor-modal-close-btn" onClick={handleClose} disabled={loading}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          {paymentSuccess ? (
            <div className="payment-success">
              <div className="success-icon">✅</div>
              <h3>Payment Successful!</h3>
              <p>Thank you for your generous {isClass ? 'dedication' : 'sponsorship'}!</p>
              {dedication && (
                <div className="dedication-display">
                  <strong>Your dedication:</strong>
                  <p>"{dedication}"</p>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handlePaymentSubmit}>
              {/* Amount Selection */}
              <div className="amount-section">
                <label className="amount-label">
                  {isClass ? 'Dedication' : 'Sponsorship'} Amount ($)
                </label>
                
                {/* Predefined amounts */}
                <div className="predefined-amounts">
                  {[18, 36, 72, 118].map((predefinedAmount) => (
                    <button
                      key={predefinedAmount}
                      type="button"
                      className={`amount-btn ${amount === predefinedAmount.toString() ? 'selected' : ''}`}
                      onClick={() => handlePredefinedAmount(predefinedAmount)}
                    >
                      ${predefinedAmount}
                    </button>
                  ))}
                </div>

                {/* Custom amount input */}
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter custom amount"
                  className="amount-input"
                  min="1"
                  step="0.01"
                  required
                />
              </div>

              {/* Dedication Message */}
              <div className="dedication-section">
                <label className="dedication-label">
                  {isClass ? 'Dedication Message' : 'Sponsorship Message'} (Optional)
                </label>
                <textarea
                  value={dedication}
                  onChange={(e) => setDedication(e.target.value)}
                  placeholder={`Add a ${isClass ? 'dedication' : 'sponsorship'} message...`}
                  className="dedication-input"
                  rows="3"
                  maxLength="500"
                />
                <div className="character-count">{dedication.length}/500</div>
              </div>

              {/* Payment Information */}
              <div className="payment-section">
                <label className="payment-label">
                  <FontAwesomeIcon icon={faCreditCard} /> Card Information
                </label>
                <div className="card-element-container">
                  {isOpen && stripe && elements && (
                    <CardElement
                      key={`card-element-${groupId}-${eventId || classId || 'general'}`}
                      options={CARD_ELEMENT_OPTIONS}
                      onChange={handleCardChange}
                    />
                  )}
                </div>
                {cardError && <div className="card-error">{cardError}</div>}
              </div>

              {/* Security Notice */}
              <div className="security-notice">
                <FontAwesomeIcon icon={faLock} />
                <span>Your payment information is secure and encrypted</span>
              </div>

              {/* Error Display */}
              {paymentError && (
                <div className="payment-error">
                  {paymentError}
                </div>
              )}

              {/* Payment Summary */}
              {amount && parseFloat(amount) > 0 && (
                <div className="payment-summary">
                  <div className="summary-row">
                    <span>{isClass ? 'Dedication' : 'Sponsorship'} Amount:</span>
                    <span>${parseFloat(amount).toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Processing Fee:</span>
                    <span>${(Math.round(parseFloat(amount) * 100 * 0.025) + 30) / 100}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total:</span>
                    <span>${parseFloat(amount).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="submit-btn"
                disabled={loading || !stripe || !cardComplete || !amount}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCreditCard} />
                    {isClass ? 'Complete Dedication' : 'Complete Sponsorship'} ${amount ? parseFloat(amount).toFixed(2) : '0.00'}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StripePaymentModal;